import type React from "react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { isPresent } from "@apl-digital/utils";
import * as Sentry from "@sentry/react";

import useWindowSize from "@/base/hooks/useWindowSize";

import ErrorPageDesktop from "./ErrorPageDesktop";
import ErrorPageMobile from "./ErrorPageMobile";
import { reloadWebsite } from "./reloadWebsite";
import type { AppError } from "./types";

type ErrorPageProps = {
  error?: AppError;
};

type DefinedAttributeValue = boolean | number | string;
type AttributeValue = DefinedAttributeValue | null | undefined;

const getDefinedAttributes = (attributes: Record<string, AttributeValue>) =>
  Object.fromEntries(
    Object.entries(attributes).filter(([, value]) => isPresent(value)),
  ) as Record<string, DefinedAttributeValue>;

const getDefaultFingerprint = (error?: AppError) => {
  if (!error) {
    return ["error-page", "unspecified"];
  }

  switch (error.type) {
    case "admin":
      return error.messageForAdmin instanceof Error
        ? [
            "error-page",
            error.type,
            error.messageForAdmin.name,
            error.messageForAdmin.message,
          ]
        : [
            "error-page",
            error.type,
            typeof error.messageForAdmin === "function"
              ? "action"
              : error.messageForAdmin,
          ];
    case "fetch":
      return [
        "error-page",
        error.type,
        String(error.httpResponseCode),
        error.message,
      ];
    case "navigation":
      return ["error-page", error.type, String(error.httpCode)];
    case "simple":
      return ["error-page", error.type, error.message];
  }
};

const getCaptureMetadata = (error?: AppError) => ({
  fingerprint: error?.sentry?.fingerprint ?? getDefaultFingerprint(error),
  message: error?.sentry?.message ?? "Showing error page",
  tags: getDefinedAttributes(error?.sentry?.tags ?? {}),
  type: error?.type ?? "unspecified",
});

const getCaptureKey = (metadata: ReturnType<typeof getCaptureMetadata>) =>
  JSON.stringify(metadata);

export const ErrorPage: React.FC<ErrorPageProps> = ({ error }) => {
  const { isMdAndUp } = useWindowSize();
  const lastCaptureKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const captureMetadata = getCaptureMetadata(error);
    const captureKey = getCaptureKey(captureMetadata);
    if (lastCaptureKeyRef.current === captureKey) {
      return;
    }
    lastCaptureKeyRef.current = captureKey;

    Sentry.withScope((scope) => {
      const sentryMetadata = error?.sentry;

      scope.setTag("error_page.type", captureMetadata.type);
      scope.setFingerprint(captureMetadata.fingerprint);

      if (sentryMetadata?.level) {
        scope.setLevel(sentryMetadata.level);
      }

      for (const [key, value] of Object.entries(captureMetadata.tags)) {
        scope.setTag(key, String(value));
      }

      for (const [key, value] of Object.entries(
        sentryMetadata?.contexts ?? {},
      )) {
        scope.setContext(key, value);
      }

      Sentry.captureException(
        new Error(captureMetadata.message, {
          cause: error,
        }),
      );

      try {
        if (sentryMetadata?.log) {
          Sentry.logger.error(
            sentryMetadata.log.message ?? captureMetadata.message,
            getDefinedAttributes(sentryMetadata.log.attributes ?? {}),
            { scope },
          );
        }
      } catch {
        // Error-page telemetry must not break the fallback UI.
      }

      for (const metric of sentryMetadata?.metrics ?? []) {
        try {
          Sentry.metrics.count(metric.name, metric.value ?? 1, {
            attributes: getDefinedAttributes(metric.attributes ?? {}),
            scope,
            unit: metric.unit,
          });
        } catch {
          // Error-page telemetry must not break the fallback UI.
        }
      }
    });
  }, [error]);

  useLayoutEffect(() => {
    const handle = setTimeout(() => {
      reloadWebsite();
    }, 3_600_000);

    return () => clearTimeout(handle);
  }, []);

  return isMdAndUp ? (
    <ErrorPageDesktop error={error} />
  ) : (
    <ErrorPageMobile error={error} />
  );
};
